import { boxStyle } from 'lib/styles/index';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import error from 'assets/error.png';
import { useQuery } from 'react-query';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Legend,
  Label,
  Tooltip,
} from 'recharts';
import palette from 'lib/styles/palette';
import { getSensorData } from 'lib/api/service/dashboard';
import useLoginCheck from 'hooks/common/useLoginCheck';

const SensorChart = () => {
  const { sensorId, name, currentTemp, currentHumid } = useSelector(
    ({ service: { sensorData } }) => ({
      sensorId: sensorData.id,
      name: sensorData.name,
      currentTemp: sensorData.currentTemp,
      currentHumid: sensorData.currentHumid,
    }),
  );

  const getSensorDataQuery = useQuery(
    ['sensorData', sensorId],
    () => getSensorData(sensorId),
    {
      enabled: !!sensorId,
    },
  );
  useLoginCheck(getSensorDataQuery.error?.response, getSensorDataQuery.refetch);
  const data = getSensorDataQuery.data?.data.data;

  return (
    <SensorChartBlock>
      {sensorId ? (
        <>
          <div className="sensor-chart-header">
            <div>{name}</div>
            <div>{currentTemp === null ? `- ˚C` : `${currentTemp}˚C`}</div>
            <div>{currentHumid === null ? `- %` : `${currentHumid}%`}</div>
          </div>
          <div className="sensor-chart-body">
            <ComposedChart width={800} height={440} data={data}>
              <XAxis dataKey="hour">
                <Label value="시간" offset={0} position="insideBottom" />
              </XAxis>
              <YAxis yAxisId="left" domain={[0, 40]}>
                <Label value="온도 ˚C" offset={0} position="insideLeft" />
              </YAxis>
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]}>
                <Label value="습도 %" position="insideRight" />
              </YAxis>
              <Bar
                yAxisId="right"
                dataKey="ih"
                barSize={15}
                fill={`${palette.theme[2]}`}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="it"
                dot={false}
                stroke={`${palette.theme[3]}`}
                strokeWidth={2}
                isAnimationActive={false}
              />
              <Legend
                verticalAlign="top"
                height={36}
                payload={[
                  {
                    value: '온도',
                    type: 'line',
                    color: `${palette.theme[3]}`,
                  },
                  {
                    value: '습도',
                    type: 'square',
                    color: `${palette.theme[2]}`,
                  },
                ]}
              />
              <Tooltip
                offset={5}
                contentStyle={{
                  background: `${palette.gray[1]}`,
                  borderRadius: 8,
                  fontSize: 14,
                }}
                labelStyle={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  marginBottom: 4,
                }}
                itemStyle={{
                  fontWeight: 500,
                  color: '#000',
                }}
                cursor={{ stroke: `${palette.gray[5]}`, strokeWidth: 2 }}
                formatter={(value, name) => {
                  if (name === 'it') {
                    return [`${value}˚C`, '온도'];
                  } else {
                    return [`${value}%`, '습도'];
                  }
                }}
                labelFormatter={value => `${value}시`}
              />
            </ComposedChart>
          </div>
        </>
      ) : (
        <div className="error-msg-wrapper">
          <img src={error} alt="error" />
          <div className="error-msg">등록된 센서가 없습니다</div>
        </div>
      )}
    </SensorChartBlock>
  );
};

export default SensorChart;

const SensorChartBlock = styled.div`
  ${boxStyle}
  min-width: 860px;
  height: 600px;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;

  .sensor-chart-header {
    margin-top: 1.5rem;
    font-size: 1.3rem;
    font-weight: bold;
    display: flex;
    align-items: flex-end;
    div:first-child {
      margin-right: 10px;
    }
    div + div {
      margin-left: 6px;
      font-size: 1.1rem;
    }
  }

  .sensor-chart-body {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 10px;
  }

  .error-msg-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  img {
    width: 500px;
  }

  .error-msg {
    margin-left: 10px;
    font-size: 1.3rem;
    font-weight: bold;
  }
`;
